// src/pages/dashboard/devices/DevicePage.tsx

import { Fragment, useEffect, useRef, useState } from "react";
import type { RootLayoutContextType } from "layouts/RootLayout";
import { Button, Card, Container, Modal, Tab, Tabs } from "react-bootstrap";
import { evalExpr } from "utils/evalExpr";
import { useTimer } from "context/TimerContext";
import { useAuth } from "context/AuthContext";
import { useParams, useOutletContext } from "react-router-dom";
import { useTimezone } from "context/TimezoneContext";
import { Bell, Gear, People, BuildingGear } from "react-bootstrap-icons";
import { useDeviceMenuRefresh } from "context/DeviceMenuRefreshContext";
import EditDeviceForm, { EditDeviceFormHandle } from "components/device/settings/EditDeviceForm";
import EditDeviceParamsForm, { EditDeviceParamsFormHandle } from "components/device/settings/EditDeviceParamsForm";
import EditCalculatedParamsForm from "components/device/settings/EditCalculatedParamsForm";
import EditStatsTab from "components/device/settings/EditStatsTab";
import ViewUsersModalForm from "components/admin/ViewUsersModalForm";
import ViewDataModal from "components/dashboard/ViewDataModal";
import UserAlertModal from "components/dashboard/UserAlertModal";

import http from "services/httpClient";
import NotFound from "../pages/NotFound";



import AddGraphModal from "components/dashboard/AddGraphModal";
import GaugeDashboard from "components/dashboard/GaugeDashboard";
const BACKEND_ORIGIN = import.meta.env.VITE_BACKEND_ORIGIN;

interface ClientFeatures {
  id: number;
  client_id: number;
  sms_enabled: boolean;
  reports_enabled: boolean;
  updated_at: string;
  updated_by_user_id: number | null;
}

interface CalculatedParam {
  result_label: string;
  column_index: number;
  unit?: string;
  expression: string;
}

interface ClientBranding {
  id: number;
  client_id: number;
  logo_path: string;
}

interface StatDefinition {
  id: number;
  result_label: string;
  source_column_index: number;
  aggregation: "min" | "max" | "avg";
  window_hours: 1 | 6 | 8 | 12 | 24;
  unit?: string;
  multiplier?: number;
  offset?: number;
}

interface StatWindow {
  min: number | null;
  max: number | null;
  avg: number | null;
  count: number;
}

interface DeviceStatsResponse {
  device_id: number;
  column_index: number;
  last_1h: StatWindow;
  last_6h: StatWindow;
  last_8h: StatWindow;
  last_12h: StatWindow;
  last_24h: StatWindow;
}

export function DevicePage() {
  const { user } = useAuth();
  const role = user?.role;
  const { timezone } = useTimezone();

  const { triggerRefresh } = useDeviceMenuRefresh();
  const { device_name } = useParams<{ device_name: string }>();

  const [graphData, setGraphData] = useState<any[]>([]);
  const [graphsLoading, setGraphsLoading] = useState(true);
  const [deviceId, setDeviceId] = useState<number | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showUsersModalForm, setshowUsersModalForm] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showDataViewer, setShowDataViewer] = useState(false);

  const [selectedDevice, setSelectedDevice] = useState<any>(null);

  const deviceParamsRef = useRef<EditDeviceParamsFormHandle>(null);
  const deviceInfoRef = useRef<EditDeviceFormHandle>(null);

  const [clientRole, setClientRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("params");
  const now = useTimer();
  const [notFound, setNotFound] = useState(false);

  // ✅ Client Settings Modal (Branding + Features)
  const [showClientSettingsModal, setShowClientSettingsModal] = useState(false);
  const [clientSettingsTab, setClientSettingsTab] = useState<"branding" | "features">("branding");

  // const [clientFeatures, setClientFeatures] = useState<ClientFeatures | null>(null);
  const [reportsEnabled, setReportsEnabled] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresError, setFeaturesError] = useState<string | null>(null);

  const [branding, setBranding] = useState<ClientBranding | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoSaving, setLogoSaving] = useState(false);

  const { setLogoUrl } = useOutletContext<RootLayoutContextType>();

  const canSeeBranding = role === "admin" || role === "owner";
  // if managers should also see branding, include: || clientRole === "manager"

  useEffect(() => {
    // If branding isn't available but it's currently selected, switch to features
    if (!canSeeBranding && clientSettingsTab === "branding") {
      setClientSettingsTab("features");
    }
  }, [canSeeBranding, clientSettingsTab]);

  // Clear old state when route changes
  useEffect(() => {
    setGraphData([]);
    setDeviceId(null);
    setSelectedDevice(null);
    setNotFound(false);
  }, [device_name]);

  // Clear features UI when client changes
  useEffect(() => {
    // setClientFeatures(null);
    setReportsEnabled(false);
  }, [selectedDevice?.client_id]);

  // Fetch branding for header logo when client changes
  useEffect(() => {
    const fetchBranding = async () => {
      if (!selectedDevice?.client_id) {
        setBranding(null);
        setLogoUrl(null);
        return;
      }

      try {
        const res = await http.get(`/client_branding/by-client/${selectedDevice.client_id}`);
        const data = res.data as ClientBranding;
        setBranding(data);
        setLogoUrl(data.logo_path);
      } catch {
        setBranding(null);
        setLogoUrl(null);
      }
    };

    fetchBranding();

    return () => {
      setLogoUrl(null);
    };
  }, [selectedDevice?.client_id, setLogoUrl]);

  // ---------- Client Features helpers ----------
  const fetchClientFeatures = async (clientId: number) => {
    try {
      const res = await http.get(`/client_features/${clientId}`);
      const data = res.data as ClientFeatures;
      // setClientFeatures(data);
      setReportsEnabled(!!data.reports_enabled);
    } catch {
      // 404 -> no row yet
      // setClientFeatures(null);
      setReportsEnabled(false);
    }
  };

  const saveClientFeatures = async () => {
    if (!selectedDevice?.client_id) return;

    setFeaturesSaving(true);
    setFeaturesError(null);

    try {
      await http.patch(`/client_features/${selectedDevice.client_id}`, {
        reports_enabled: reportsEnabled,
      });

      triggerRefresh(); // refresh sidebar/menu
      setShowClientSettingsModal(false); // ✅ feels responsive
    } catch {
      setFeaturesError("Failed to save client features.");
    } finally {
      setFeaturesSaving(false);
    }
  };

  // ---------- Branding helpers ----------
  const MAX_LOGO_SIZE_BYTES = 300 * 1024;

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogoError(null);
    const file = e.target.files?.[0];

    if (!file) {
      setLogoFile(null);
      return;
    }

    if (!["image/png", "image/jpeg", "image/svg+xml"].includes(file.type)) {
      setLogoError("Only PNG, JPEG, or SVG images are allowed.");
      setLogoFile(null);
      return;
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setLogoError(`File too large. Max size is ${Math.round(MAX_LOGO_SIZE_BYTES / 1024)} KB.`);
      setLogoFile(null);
      return;
    }

    setLogoFile(file);
  };

  const handleSaveLogo = async () => {
    if (!selectedDevice?.client_id) return;
    if (!logoFile) {
      setLogoError("Please select an image file first.");
      return;
    }

    setLogoSaving(true);
    setLogoError(null);

    try {
      const formData = new FormData();
      formData.append("file", logoFile);

      const uploadRes = await http.post(`/upload/logo/${selectedDevice.client_id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const logoPath: string = uploadRes.data.path;

      if (branding) {
        const updateRes = await http.put<ClientBranding>(`/client_branding/${branding.id}`, {
          logo_path: logoPath,
        });
        setBranding(updateRes.data);
        setLogoUrl(updateRes.data.logo_path);
      } else {
        const createRes = await http.post<ClientBranding>(`/client_branding/`, {
          client_id: selectedDevice.client_id,
          logo_path: logoPath,
        });
        setBranding(createRes.data);
        setLogoUrl(createRes.data.logo_path);
      }

      setShowClientSettingsModal(false);
    } catch {
      setLogoError("Failed to save logo. Please try again.");
    } finally {
      setLogoSaving(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!branding) return;

    setLogoSaving(true);
    setLogoError(null);

    try {
      await http.delete(`/client_branding/${branding.id}`);
      setBranding(null);
      setLogoFile(null);
      setLogoUrl(null);
      setShowClientSettingsModal(false);
    } catch {
      setLogoError("Failed to delete logo. Please try again.");
    } finally {
      setLogoSaving(false);
    }
  };

  const openClientSettingsModal = async () => {
    if (!selectedDevice?.client_id) return;

    setLogoError(null);
    setLogoFile(null);
    setFeaturesError(null);

    // branding (only fetch if user can actually see branding)
    if (canSeeBranding) {
      try {
        const res = await http.get(`/client_branding/by-client/${selectedDevice.client_id}`);
        setBranding(res.data as ClientBranding);
      } catch {
        setBranding(null);
      }
    } else {
      // ensure branding state doesn't keep UI "thinking" it should show branding
      setBranding(null);
    }

    // features (always)
    await fetchClientFeatures(selectedDevice.client_id);

    // ✅ IMPORTANT: set the initial tab to something that exists
    setClientSettingsTab(canSeeBranding ? "branding" : "features");

    setShowClientSettingsModal(true);
  };

  // ---------- Client Role ----------
  useEffect(() => {
    if (!selectedDevice?.client_id) return;

    const fetchClientRole = async () => {
      try {
        const res = await http.get(`/clients/${selectedDevice.client_id}/role`);
        setClientRole(res.data);
      } catch {
        // ignore
      }
    };

    fetchClientRole();
  }, [selectedDevice?.client_id]);

  // ---------- Device lookup ----------
  useEffect(() => {
    const fetchDeviceId = async () => {
      try {
        const response = await http.get(`/device?sort_by=device`);
        const devices = await response.data;
        const device = devices.find((d: any) => d.device_name === device_name);

        if (device) {
          setDeviceId(device.id);
          setSelectedDevice(device);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      } catch {
        // ignore
      }
    };

    fetchDeviceId();
  }, [device_name]);

  const getStatValueFromResponse = (
    stats: DeviceStatsResponse | undefined,
    aggregation: "min" | "max" | "avg",
    windowHours: 1 | 6 | 8 | 12 | 24
  ): number | null => {
    if (!stats) return null;

    const blockMap: Record<number, StatWindow | undefined> = {
      1: stats.last_1h,
      6: stats.last_6h,
      8: stats.last_8h,
      12: stats.last_12h,
      24: stats.last_24h,
    };

    const block = blockMap[windowHours];
    if (!block) return null;

    if (aggregation === "avg") return block.avg ?? null;
    if (aggregation === "min") return block.min ?? null;
    if (aggregation === "max") return block.max ?? null;

    return null;
  };

  const requireManager = async (): Promise<boolean> => {
    // Global owner/admin bypass client-role checking
    if (role === "owner" || role === "admin") {
      return true;
    }
    
    if (!selectedDevice?.client_id) return false;

    try {
      const res = await http.get(
        `/clients/${selectedDevice.client_id}/role`
      );

      const currentRole = res.data;
      setClientRole(currentRole);

      if (currentRole !== "manager") {
        alert("You no longer have permission to perform this action.");
        return false;
      }

      return true;
    } catch {
      alert("Unable to verify your permissions.");
      return false;
    }
  };

  const loadStatsCache = async (
    statDefs: StatDefinition[],
    deviceId: number
  ): Promise<Record<number, DeviceStatsResponse>> => {
    const uniqueSourceColumns = [...new Set(statDefs.map((s) => s.source_column_index))];
    
    const entries = await Promise.all(
      uniqueSourceColumns.map(async (sourceColumnIndex) => {
        const res = await http.get(`/device-data/${deviceId}/stats`, {
          params: { column_index: sourceColumnIndex, tz: timezone, },
        });
        return [sourceColumnIndex, res.data as DeviceStatsResponse] as const;
      })
    );

    return Object.fromEntries(entries);
  };

  // ---------- Latest-only polling ----------
  const fetchLatestOnly = async () => {
    if (!deviceId) return;

    try {
      const res = await http.get(`/data/${deviceId}/data?range=latest`);
      const latest = await res.data;

      if (!latest.length) return;
      if (!graphData.length) return;

      const statGaugeColumnIndexes = graphData
        .map((g) => {
          const config = g.graph_config || {};

          return (
            config.label_column_index ??
            config.column_index ??
            config.device_label?.column_index
          );
        })
        .filter((c: any) => typeof c === "number" && c >= 1000);

      let statDefs: StatDefinition[] = [];
      let statsCache: Record<number, DeviceStatsResponse> = {};

      if (statGaugeColumnIndexes.length > 0) {
        try {
          const statRes = await http.get(`/stat-definitions/device/${deviceId}`);
          statDefs = (statRes.data || []) as StatDefinition[];

          const usedStatDefs = statDefs.filter((s) =>
            statGaugeColumnIndexes.includes(1000 + s.id)
          );

          statsCache = await loadStatsCache(usedStatDefs, deviceId);
        } catch {
          statDefs = [];
          statsCache = {};
        }
      }

      const updated = graphData.map((graph) => {
        const config = graph.graph_config || {};

        if (
          config.type !== "solid_gauge" &&
          config.type !== "boolean_indicator" &&
          config.type !== "temperature_gauge"
        ) {
          return graph;
        }

        const colIndex =
          config.label_column_index ??
          config.column_index ??
          config.device_label?.column_index;
        const dataValues = latest[0].data_values;
        let value: number | null = null;

        if (colIndex >= 1000) {
          const statId = colIndex - 1000;
          const stat = statDefs.find((s) => s.id === statId);

          if (stat) {
            const stats = statsCache[stat.source_column_index];
            const raw = getStatValueFromResponse(stats, stat.aggregation, stat.window_hours);
            value = raw !== null ? raw * (stat.multiplier ?? 1) + (stat.offset ?? 0) : null;
          } else {
            value = null;
          }
        } else if (colIndex >= 100) {
          const calc = config.device_label;
          if (calc?.expression) {
            try {
              const paramVars: Record<string, number> = {};
              for (let i = 0; i < 20; i++) paramVars[`x${i}`] = dataValues[i] ?? 0;
              value = evalExpr(calc.expression, paramVars);
            } catch {}
          }
        } else {
          const rawValue = dataValues[colIndex];
          value =
            rawValue !== undefined && config.device_label
              ? rawValue * (config.device_label.multiplier ?? 1) + (config.device_label.offset ?? 0)
              : null;
        }

        return {
          ...graph,
          graph_config: {
            ...graph.graph_config,
            value,
            timestamp: latest[0].timestamp,
          },
        };
      });

      setGraphData(updated);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!deviceId) return;
    fetchLatestOnly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, graphData.length, now]);

  // ---------- Graph fetching ----------
  const buildEnrichedGraphs = (
    graphs: any[],
    allLabels: any[],
    statLabels: any[],
    calcLabels: any[],
    latest: any[],
    triggers: { column_index: number; threshold: number; operator: string }[],
    statsCache: Record<number, DeviceStatsResponse>
  ) =>
    graphs.map((graph: any) => {
      const config = graph.graph_config || {};
      const colIndex =
        config.label_column_index ??
        config.column_index ??
        config.device_label?.column_index;

      let device_label = null;
      let chart_labels: any[] = [];
      let value: number | null = null;
      let unit = "";
      let timestamp = latest[0]?.timestamp || null;

      const trigger = triggers.find((t) => t.column_index === colIndex) || null;

      if (
        config.type === "solid_gauge" ||
        config.type === "boolean_indicator" ||
        config.type === "temperature_gauge"
      ) {
        device_label = allLabels.find((l: any) => l.column_index === colIndex) || null;

        if (colIndex !== undefined && latest[0]?.data_values) {
          const dataValues = latest[0].data_values;

          if (colIndex >= 1000) {
            const stat = statLabels.find((s: any) => s.column_index === colIndex);
            if (stat) {
              const stats = statsCache[stat.source_column_index];
              const raw = getStatValueFromResponse(stats, stat.aggregation, stat.window_hours);
              value = raw !== null ? raw * (stat.multiplier ?? 1) + (stat.offset ?? 0) : null;
              unit = stat.unit || "";
            }
          } else if (colIndex >= 100) {
            const calc = calcLabels.find((c: any) => c.column_index === colIndex);
            if (calc?.expression) {
              try {
                const paramVars: Record<string, number> = {};
                for (let i = 0; i < 20; i++) paramVars[`x${i}`] = dataValues[i] ?? 0;
                value = evalExpr(calc.expression, paramVars);
                unit = calc.unit || "";
              } catch {
                value = null;
              }
            }
          } else {
            const rawValue = dataValues[colIndex];
            value =
              rawValue !== undefined && device_label
                ? rawValue * (device_label.multiplier ?? 1) + (device_label.offset ?? 0)
                : null;
            unit = device_label?.unit || "";
          }
        }

        unit = device_label?.unit || unit;
      }

      if (config.type === "standard_chart" || config.type === "windrose_chart") {
        const chartLabelsRaw = Array.isArray(config.chart_labels) ? config.chart_labels : [];
        chart_labels = chartLabelsRaw.map((item: any) => {
          const cIndex = typeof item === "object" ? item.column_index : item;
          const color = typeof item === "object" ? item.color : "#ffffff";
          const label = allLabels.find((l: any) => l.column_index === cIndex);
          if (!label) {
            return {
              column_index: cIndex,
              label:
                cIndex >= 1000 ? `Stat ${cIndex}` : cIndex >= 100 ? `Calc ${cIndex}` : `Col ${cIndex + 1}`,
              is_enabled: true,
              unit: "",
              color,
            };
          }
          return { ...label, multiplier: label.multiplier ?? 1, offset: label.offset ?? 0, color };
        });
      }

      return {
        ...graph,
        graph_config: {
          ...config,
          device_label,
          chart_labels,
          unit,
          value,
          timestamp,
          threshold: trigger?.threshold ?? null,
          operator: trigger?.operator ?? null,
        },
      };
    });

  const fetchGraphs = async () => {
    if (!deviceId) return;

    setGraphsLoading(true);
    try {
      // Phase 1: all requests except statsCache — renders everything immediately
      const [graphsRes, labelsRes, calcRes, latestRes, triggersRes, statRes] = await Promise.all([
        http.get(`/graphs/${deviceId}`),
        http.get(`/device/${deviceId}/labels`),
        http.get(`/calculated_params/${deviceId}`),
        http.get(`/data/${deviceId}/data?range=latest`),
        http.get(`/triggers/device/${deviceId}`),
        http.get(`/stat-definitions/device/${deviceId}`),
      ]);

      const rawLabels = labelsRes.data;
      const calcData = calcRes.data as CalculatedParam[];
      const calcLabels = calcData.map((c) => ({
        label: c.result_label,
        column_index: c.column_index,
        is_enabled: true,
        unit: c.unit || "",
        multiplier: 1,
        offset: 0,
        expression: c.expression,
      }));

      const statDefs = (statRes.data || []) as StatDefinition[];
      const statLabels = statDefs.map((s: any) => ({
        label: s.result_label,
        column_index: 1000 + s.id,
        is_enabled: true,
        unit: s.unit || "",
        multiplier: s.multiplier ?? 1,
        offset: s.offset ?? 0,
        kind: "stat",
        source_column_index: s.source_column_index,
        aggregation: s.aggregation,
        window_hours: s.window_hours,
      }));

      const allLabels = [...rawLabels, ...calcLabels, ...statLabels];
      const graphs = graphsRes.data;
      const latest = latestRes.data;
      const triggers = triggersRes.data as { column_index: number; threshold: number; operator: string }[];

      // Render all graphs now — stat-based gauges will show null until phase 2
      setGraphData(buildEnrichedGraphs(graphs, allLabels, statLabels, calcLabels, latest, triggers, {}));
      setGraphsLoading(false);

      // Phase 2: fetch stat rolling windows (slowest part) — updates stat-based gauge values
      if (statDefs.length > 0) {
        try {
          const statsCache = await loadStatsCache(statDefs, deviceId);
          setGraphData(buildEnrichedGraphs(graphs, allLabels, statLabels, calcLabels, latest, triggers, statsCache));
        } catch {
          // stat values stay null — non-blocking
        }
      }
    } catch {
      // ignore
    } finally {
      setGraphsLoading(false);
    }
  };

  useEffect(() => {
    if (deviceId) fetchGraphs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const deleteGraph = async (graphId: number) => {
    try {
      const response = await http.delete(`/graphs/${graphId}`);
      if (response.status === 200) fetchGraphs();
    } catch {
      // ignore
    }
  };

  if (notFound) return <NotFound />;

  return (
    <Fragment>
      <div className="bg-primary pt-5 pb-21"></div>

      <Container fluid className="mt-n22 px-2">
        <div className="d-flex justify-content-center">
          <Card className="w-100">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Device: {device_name}</h4>

                <div className="d-flex gap-2">
                  {(role === "admin" || role === "owner" || clientRole === "manager") && (
                    <Button
                      variant="outline-secondary"
                      onClick={async () => {
                          if (await requireManager()) {
                            openClientSettingsModal();
                          }
                        }}
                      title="Client Settings"
                    >
                      <BuildingGear />
                    </Button>
                  )}

                  {(role === "admin" ||
                    role === "owner" ||
                    (role === "client" && clientRole === "manager")) && (
                    <>
                      <Button
                        variant="outline-warning"
                        onClick={async () => {
                          if (await requireManager()) {
                            setShowTriggerModal(true);
                          }
                        }}
                        title="Manage Alerts"
                      >
                        <Bell />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={async () => {
                          if (await requireManager()) {
                            setshowUsersModalForm(true);
                          }
                        }}
                        title="User Settings"
                      >
                        <People />
                      </Button>

                      <Button
                        variant="outline-secondary"
                        onClick={async () => {
                          if (await requireManager()) {
                            setShowSettingsModal(true);
                          }
                        }}
                        title="Device Settings"
                      >
                        <Gear />
                      </Button>

                      <Button variant="primary"
                        onClick={async () => {
                          if (await requireManager()) {
                            setShowModal(true);
                          }
                        }}>
                        Add Graph
                      </Button>
                    </>
                  )}

                  <Button variant="outline-secondary" 
                    onClick={() => setShowDataViewer(true)}>
                    View Data
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body
              className="p-0 d-flex flex-column"
              style={{ height: "calc(100vh - 170px)", overflow: "hidden" }}
            >
              {/* ✅ DEVICE SETTINGS MODAL */}
              <Modal
                show={showSettingsModal}
                onHide={() => setShowSettingsModal(false)}
                size="lg"
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>Device Settings</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k || "params")}
                    id="device-settings-tabs"
                    className="mb-3"
                  >
                    <Tab eventKey="params" title="Device Parameters">
                      <EditDeviceParamsForm ref={deviceParamsRef} deviceId={deviceId!} />
                    </Tab>
                    <Tab eventKey="info" title="Device Info">
                      <EditDeviceForm ref={deviceInfoRef} device={selectedDevice} />
                    </Tab>
                    <Tab eventKey="calculated" title="Calculated Params">
                      <EditCalculatedParamsForm deviceId={deviceId!} />
                    </Tab>
                    <Tab eventKey="stats" title="Stats">
                      <EditStatsTab deviceId={deviceId!} />
                    </Tab>
                  </Tabs>
                </Modal.Body>

                <Modal.Footer>
                  {["params", "info"].includes(activeTab) && (
                    <Button
                      variant="primary"
                      onClick={async () => {
                        try {
                          await deviceParamsRef.current?.save();
                          await deviceInfoRef.current?.save();
                          const updatedDevice = await deviceInfoRef.current?.save();
                          if (updatedDevice) setSelectedDevice(updatedDevice);

                          await fetchGraphs();
                          setShowSettingsModal(false);
                        } catch {
                          alert("Error saving parameters.");
                        }
                        triggerRefresh();
                      }}
                    >
                      Save Changes
                    </Button>
                  )}
                </Modal.Footer>
              </Modal>

              {/* ✅ CLIENT SETTINGS MODAL (Branding + Features) */}
              <Modal
                show={showClientSettingsModal}
                onHide={() => setShowClientSettingsModal(false)}
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>Client Settings</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  <Tabs
                    activeKey={clientSettingsTab}
                    onSelect={(k) => setClientSettingsTab((k as any) ?? (canSeeBranding ? "branding" : "features"))}
                    className="mb-3"
                  > 
                    {canSeeBranding && (
                      <Tab eventKey="branding" title="Branding">
                        {branding?.logo_path ? (
                          <div className="mb-3 text-center">
                            <img
                              src={branding ? `${BACKEND_ORIGIN}${branding.logo_path}` : ""}
                              alt="Client Logo"
                              style={{ maxHeight: "80px", maxWidth: "100%", objectFit: "contain" }}
                            />
                          </div>
                        ) : (
                          <p className="text-muted">
                            No logo set yet. Select an image to upload a logo for this client.
                          </p>
                        )}

                        <div className="mb-3">
                          <label className="form-label">Select image</label>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/png,image/jpeg,image/svg+xml"
                            onChange={handleLogoFileChange}
                          />
                        </div>

                        {logoError && <div className="alert alert-danger py-2">{logoError}</div>}

                        <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                          Allowed types: PNG, JPEG, SVG. Max size:{" "}
                          {Math.round(MAX_LOGO_SIZE_BYTES / 1024)} KB.
                        </div>
                      </Tab>
                    )}

                    <Tab eventKey="features" title="Features">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <div className="fw-semibold">Enable Reports</div>
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            If disabled, the Reports page/menu should be hidden for this client.
                          </div>
                        </div>

                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={reportsEnabled}
                            disabled={featuresSaving}   // ✅
                            onChange={(e) => setReportsEnabled(e.target.checked)}
                          />
                        </div>
                      </div>

                      {featuresError && (
                        <div className="alert alert-danger py-2 mt-3">{featuresError}</div>
                      )}
                    </Tab>
                  </Tabs>
                </Modal.Body>

                <Modal.Footer>
                  {clientSettingsTab === "branding" && branding && (
                    <Button
                      variant="outline-danger"
                      onClick={handleDeleteLogo}
                      disabled={logoSaving}
                    >
                      Delete Logo
                    </Button>
                  )}

                  <Button
                    variant="secondary"
                    onClick={() => setShowClientSettingsModal(false)}
                    disabled={logoSaving || featuresSaving}
                  >
                    Close
                  </Button>

                  {clientSettingsTab === "branding" ? (
                    <Button variant="primary" onClick={handleSaveLogo} disabled={logoSaving}>
                      {logoSaving ? "Saving..." : "Save Logo"}
                    </Button>
                  ) : (
                    <Button variant="primary" onClick={saveClientFeatures} disabled={featuresSaving}>
                      {featuresSaving ? "Saving..." : "Save Features"}
                    </Button>
                  )}
                </Modal.Footer>
              </Modal>

              {/* Main content */}
              <div className="flex-grow-1" style={{ overflowY: "auto", padding: "1rem" }}>
                {graphsLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="spinner-border text-dark" role="status" style={{ width: "3rem", height: "3rem" }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : graphData.length === 0 ? (
                  <p className="text-center">No graphs yet. Click "Add Graph" to create one.</p>
                ) : (
                  <GaugeDashboard
                    key={deviceId}
                    gauges={graphData}
                    onDeleteGraph={deleteGraph}
                    deviceId={deviceId!}
                    clientRole={clientRole}
                  />
                )}
              </div>

              {/* Other modals/components */}
              {deviceId && (
                <AddGraphModal
                  show={showModal}
                  handleClose={() => setShowModal(false)}
                  deviceId={deviceId}
                  refetchGraphs={fetchGraphs}
                />
              )}

              {showDataViewer && deviceId && (
                <ViewDataModal
                  show={showDataViewer}
                  onHide={() => setShowDataViewer(false)}
                  deviceId={deviceId}
                  deviceName={device_name || "device"}
                />
              )}

              {selectedDevice?.client_id && (
                <ViewUsersModalForm
                  clientId={selectedDevice.client_id}
                  show={showUsersModalForm}
                  onClose={() => setshowUsersModalForm(false)}
                  onUserChange={() => {
                    // optional: refresh device if needed
                  }}
                />
              )}

              {deviceId && (
                <UserAlertModal
                  deviceId={deviceId}
                  clientId={selectedDevice.client_id}  
                  show={showTriggerModal}
                  onClose={() => setShowTriggerModal(false)}
                />
              )}
            </Card.Body>
          </Card>
        </div>
      </Container>
    </Fragment>
  );
}

export default DevicePage;